import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Code,
  CloudQueue,
  Security,
  Speed,
  Analytics,
  Business,
  CheckCircle,
  ExpandMore,
  TrendingUp,
  Storage
} from '@mui/icons-material';

const ApplicationModernization = () => {
  const benefits = [
    'Unlock the value of your digital business',
    'Improve application performance and reliability',
    'Reduce maintenance costs and technical debt',
    'Enable faster time-to-market for new features',
    'Enhance security and compliance posture',
    'Support modern development practices'
  ];

  const services = [
    {
      title: 'Legacy System Assessment',
      description: 'Comprehensive analysis of your existing applications to identify modernization opportunities',
      features: ['Technical debt analysis', 'Performance evaluation', 'Security assessment', 'Migration planning']
    },
    {
      title: 'Cloud-Native Development',
      description: 'Transform monolithic applications into scalable, cloud-native architectures',
      features: ['Microservices architecture', 'Container orchestration', 'API-first design', 'DevOps integration']
    },
    {
      title: 'API Modernization',
      description: 'Modernize and secure your APIs for better integration and performance',
      features: ['API gateway implementation', 'Security hardening', 'Performance optimization', 'Documentation']
    },
    {
      title: 'Database Modernization',
      description: 'Upgrade and optimize your database infrastructure for better performance',
      features: ['Database migration', 'Performance tuning', 'Cloud database services', 'Backup and recovery']
    }
  ];

  const faqs = [
    {
      question: 'What is application modernization?',
      answer: 'Application modernization is the process of updating legacy software applications to use modern computing approaches, including cloud-native architectures, microservices, and modern development practices. This helps organizations improve performance, reduce costs, and enable digital transformation.'
    },
    {
      question: 'How long does application modernization take?',
      answer: 'The timeline for application modernization varies depending on the complexity and size of the applications. Simple applications may take 3-6 months, while complex enterprise systems can take 12-24 months. We provide detailed project timelines during the assessment phase.'
    },
    {
      question: 'What are the benefits of modernizing applications?',
      answer: 'Modernized applications offer improved performance, better security, reduced maintenance costs, enhanced scalability, and faster development cycles. They also enable organizations to adopt modern technologies like AI, machine learning, and advanced analytics.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 3
            }}
          >
            Application Modernization
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Unlock the value of your digital business with comprehensive application transformation services
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Why Modernize Your Applications?
          </Typography>
          
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircle sx={{ color: '#4caf50', mr: 2, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {benefit}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Our Modernization Services
          </Typography>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                    <List>
                      {service.features.map((feature, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  mb: 2,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'white', borderRadius: '0 0 8px 8px' }}>
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Ready to Modernize Your Applications?
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Get a comprehensive assessment of your application modernization opportunities
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Request Assessment
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default ApplicationModernization;
